import { GoalType, IncomeAndExpensesType, NetWealthType } from '../core/enums';
// eslint-disable-next-line import/no-cycle
import { age45OrOlder, ageUnder45, defaultMoneyValue } from '../core/constants';

export interface ClientBase {
	preferredName: string;
	firstName: string;
	lastName: string;
	email: string;
}

export default interface Client extends ClientBase {
	ageGroup?: string;
	isBusinessOwner: boolean;
	isClient: boolean;
	directorId?: number;
	officeId?: number;
}

export interface ExtendedClient extends Client {
	isOver45: boolean;
}

export const createExtendedClient = (client: Client = null): ExtendedClient => {
	if (client)
		return {
			...client,
			isOver45: client.ageGroup === age45OrOlder
		};
	return {
		preferredName: '',
		firstName: '',
		lastName: '',
		email: '',
		isOver45: false,
		isBusinessOwner: true,
		isClient: true,
		officeId: null,
		directorId: null
	};
};

export const createClientFromExtendedClient = (extendedClient: ExtendedClient): Client => {
	if (!extendedClient) return null;
	const { isOver45, ...client } = extendedClient;
	return {
		...client,
		ageGroup: isOver45 ? age45OrOlder : ageUnder45
	};
};

export interface FamilyInformation {
	includeEducationCostsProvisions: boolean | null;
	shareProfileToFamilyMembers: boolean;
}

export const createDefaultFamilyInformation = (familyCommonDetails: FamilyInformation = null): FamilyInformation => {
	return {
		includeEducationCostsProvisions: familyCommonDetails?.includeEducationCostsProvisions ?? false,
		shareProfileToFamilyMembers: familyCommonDetails?.shareProfileToFamilyMembers ?? true
	};
};

export interface ClientEstateRelationship {
	haveBeenInMarriageOrDeFactoRelationship: boolean | null;
	divorceDate: Date | string | null;
	propertyOrderDate: Date | string | null;
	childrenDetailsOfPriorRelationships: string;
	ongoingFinancialCommitments: string;
	childSpecialNeeds: string;
}

export const createDefaultClientEstateRelationship = (
	relationship: ClientEstateRelationship = null
): ClientEstateRelationship => {
	return {
		haveBeenInMarriageOrDeFactoRelationship: relationship?.haveBeenInMarriageOrDeFactoRelationship ?? false,
		divorceDate: relationship?.divorceDate ?? null,
		propertyOrderDate: relationship?.propertyOrderDate ?? null,
		childrenDetailsOfPriorRelationships: relationship?.childrenDetailsOfPriorRelationships ?? null,
		ongoingFinancialCommitments: relationship?.ongoingFinancialCommitments ?? null,
		childSpecialNeeds: relationship?.childSpecialNeeds ?? null
	};
};

export interface FamilyMember {
	clientId: number;
	firstName: string;
	lastName: string;
	dateOfBirth: Date | string | null;
	relationship: string;
	financiallyDependent: boolean | null;
	supportToAge: number | null;
	schoolOrUniFees: number | null;
	atSchool: boolean | null;
	numberOfChildren: number | null;
	childrenDetails: string;
	tfn: string;
	abn: string;
	email: string;
	mobilePhone?: string;
	canAccessFamilyData: boolean;
	shareProfileToFamilyMembers: boolean;
}

export const createDefaultFamilyMember = (familyMember: FamilyMember = null): FamilyMember => {
	return {
		clientId: familyMember?.clientId ?? null,
		firstName: familyMember?.firstName ?? null,
		lastName: familyMember?.lastName ?? null,
		dateOfBirth: familyMember?.dateOfBirth ?? null,
		relationship: familyMember?.relationship ?? null,
		financiallyDependent: familyMember?.financiallyDependent ?? false,
		supportToAge: familyMember?.supportToAge ?? 18, // default to 18
		schoolOrUniFees: familyMember?.schoolOrUniFees ?? null,
		atSchool: familyMember?.atSchool ?? false,
		numberOfChildren: familyMember?.numberOfChildren ?? 0,
		childrenDetails: familyMember?.childrenDetails ?? null,
		tfn: familyMember?.tfn ?? null,
		abn: familyMember?.abn ?? null,
		email: familyMember?.email ?? null,
		mobilePhone: familyMember?.mobilePhone ?? null,
		canAccessFamilyData: familyMember?.canAccessFamilyData ?? false,
		shareProfileToFamilyMembers: familyMember?.shareProfileToFamilyMembers ?? true
	};
};

export interface PersonalFinance {
	homeLoanHasOffsetAccount: boolean;
	loanRepaymentsVariationsHaveAssociatedFees: boolean;
	variationsAssociatedFeesDetails: string;
	haveMadeExtraPaymentsIntoHomeLoanOrOffset: boolean;
	amountImmediateAccessToHomeLoanOrOffset: number | null;
	canCreditSalaryDirectlyIntoHomeLoanOrOffset: boolean;
	extraCashToPayOffDebt: number | null;
	payOffCreditCardsWithinInterestFreePeriod: boolean;
	creditCardsInterestFreePeriods: string;
	debtPositionReviewedTime?: string;
	canReviewLoansForFree?: boolean;
}

export const createDefaultPersonalFinance = (personalFinance: PersonalFinance = null): PersonalFinance => {
	return {
		homeLoanHasOffsetAccount: personalFinance?.homeLoanHasOffsetAccount ?? false,
		loanRepaymentsVariationsHaveAssociatedFees: personalFinance?.loanRepaymentsVariationsHaveAssociatedFees ?? false,
		variationsAssociatedFeesDetails: personalFinance?.variationsAssociatedFeesDetails ?? null,
		haveMadeExtraPaymentsIntoHomeLoanOrOffset: personalFinance?.haveMadeExtraPaymentsIntoHomeLoanOrOffset ?? false,
		amountImmediateAccessToHomeLoanOrOffset: personalFinance?.amountImmediateAccessToHomeLoanOrOffset,
		canCreditSalaryDirectlyIntoHomeLoanOrOffset: personalFinance?.canCreditSalaryDirectlyIntoHomeLoanOrOffset ?? false,
		extraCashToPayOffDebt: personalFinance?.extraCashToPayOffDebt,
		payOffCreditCardsWithinInterestFreePeriod: personalFinance?.payOffCreditCardsWithinInterestFreePeriod ?? false,
		creditCardsInterestFreePeriods: personalFinance?.creditCardsInterestFreePeriods ?? null,
		debtPositionReviewedTime: personalFinance?.debtPositionReviewedTime ?? null,
		canReviewLoansForFree: personalFinance?.canReviewLoansForFree ?? false
	};
};

export interface ClientGoal {
	goalId: number;
	goalType: GoalType;
	clientId: number;
	goalName: string;
	startDate: Date | null;
	endDate: Date | null;
	estimatedCost: number | null;
	frequency?: string;
}

export const createDefaultClientGoal = (clientGoal: ClientGoal = null): ClientGoal => {
	return {
		goalId: clientGoal?.goalId ?? null,
		goalType: clientGoal?.goalType ?? null,
		clientId: clientGoal?.clientId ?? null,
		goalName: clientGoal?.goalName ?? null,
		startDate: clientGoal?.startDate ?? null,
		endDate: clientGoal?.endDate ?? null,
		estimatedCost: clientGoal?.estimatedCost ?? null,
		frequency: clientGoal?.frequency ?? null
	};
};

export interface GoalInformation {
	requiredCashReserve: boolean | null;
	reserveAmount: number | null;
	goalComments: string | null;
}

export const createDefaultGoalInformation = (goalCommonDetails: GoalInformation = null): GoalInformation => {
	return {
		requiredCashReserve: goalCommonDetails?.requiredCashReserve ?? false,
		reserveAmount: goalCommonDetails?.reserveAmount ?? null,
		goalComments: goalCommonDetails?.goalComments ?? null
	};
};

// Client Retirement

export interface RetirementPlan {
	retirementAge: number | null;
	retirementDate: Date | null;
	retirementAmount: number | null;
	isYearly: boolean | null;
}

export const createDefaultRetirementPlan = (retirementPlan: RetirementPlan = null): RetirementPlan => {
	return {
		retirementAge: retirementPlan?.retirementAge ?? null,
		retirementDate: retirementPlan?.retirementDate ?? null,
		retirementAmount: retirementPlan?.retirementAmount ?? null,
		isYearly: retirementPlan?.isYearly ?? false
	};
};

export interface RetirementActivity {
	activityId: number;
	clientId: number;
	activityName: string | null;
	estimatedCost: number | null;
}

export const createDefaultRetirementActivity = (retirementActivity: RetirementActivity = null): RetirementActivity => {
	return {
		activityId: retirementActivity?.activityId ?? null,
		clientId: retirementActivity?.clientId ?? null,
		activityName: retirementActivity?.activityName ?? null,
		estimatedCost: retirementActivity?.estimatedCost ?? null
	};
};

// Client Net Wealth

export interface ClientWealth {
	clientNetWealthId: number;
	clientId?: number;
	clientGroupId: number;
	entityId?: number;
	type: NetWealthType;
	category: string;
	subCategory: string;
	nameOrDescription: string;
	estimatedValue: number | null;
	superAccountNumber?: string;
	superValueDate?: Date;
	superType?: string;
	superPensionPaying?: string;
	cashAssetBank?: string;
	cashAssetInterestRate?: number;
	cashAssetMaturityDate?: Date;
	shareDateAcquired?: Date;
	shareInitialAmount?: number;
	shareUnits?: number;
	propertyDateAcquired?: Date;
	propertyPurchasePrice?: number;
	propertyAnnualRent?: number;
	propertyAnnualExpenses?: number;
	cashAssetType?: string;
	shareAssetType?: string;
	haveTransitionalRbl?: boolean | null;
	transitionalRblDetails?: string;
	haveNominatedBeneficiary?: boolean | null;
	beneficiaryName?: string;
	combinedCategory?: string;
	percentageAllocation?: number;
	relatedNetWealthId?: number;
	ownerDetail?: string;
}

export interface NetWealthOptions {
	value?: string;
	text: string;
	category?: string;
	type?: NetWealthType;
}

export const createDefaultClientWealth = (clientWealth: ClientWealth = null): ClientWealth => {
	return {
		clientNetWealthId: clientWealth?.clientNetWealthId ?? null,
		clientId: clientWealth?.clientId ?? null,
		clientGroupId: clientWealth?.clientGroupId ?? null,
		entityId: clientWealth?.entityId ?? null,
		type: clientWealth?.type ?? null,
		category: clientWealth?.category ?? null,
		subCategory: clientWealth?.subCategory ?? null,
		nameOrDescription: clientWealth?.nameOrDescription ?? null,
		estimatedValue: clientWealth?.estimatedValue ?? null,
		superAccountNumber: clientWealth?.superAccountNumber,
		superValueDate: clientWealth?.superValueDate,
		superType: clientWealth?.superType ?? null,
		superPensionPaying: clientWealth?.superPensionPaying ?? null,
		cashAssetBank: clientWealth?.cashAssetBank ?? null,
		cashAssetInterestRate: clientWealth?.cashAssetInterestRate,
		cashAssetMaturityDate: clientWealth?.cashAssetMaturityDate,
		shareDateAcquired: clientWealth?.shareDateAcquired,
		shareInitialAmount: clientWealth?.shareInitialAmount,
		shareUnits: clientWealth?.shareUnits,
		propertyDateAcquired: clientWealth?.propertyDateAcquired,
		propertyPurchasePrice: clientWealth?.propertyPurchasePrice,
		propertyAnnualRent: clientWealth?.propertyAnnualRent,
		propertyAnnualExpenses: clientWealth?.propertyAnnualExpenses,
		cashAssetType: clientWealth?.cashAssetType ?? null,
		shareAssetType: clientWealth?.shareAssetType ?? null,
		haveTransitionalRbl: clientWealth?.haveTransitionalRbl ?? false,
		transitionalRblDetails: clientWealth?.transitionalRblDetails ?? null,
		haveNominatedBeneficiary: clientWealth?.haveNominatedBeneficiary ?? false,
		beneficiaryName: clientWealth?.beneficiaryName ?? null,
		combinedCategory:
			clientWealth?.category && clientWealth?.subCategory
				? `${clientWealth?.category} - ${clientWealth?.subCategory}`
				: '',
		percentageAllocation: clientWealth?.percentageAllocation ?? null,
		relatedNetWealthId: clientWealth?.relatedNetWealthId ?? null,
		ownerDetail: clientWealth?.ownerDetail ?? null
	};
};

export interface WealthInformation {
	netWealthComments: string | null;
	netWealthAmount: string | null; // Use string to store formatted number with comma
}

export const createDefaultWealthInformation = (wealthCommonDetails: WealthInformation = null): WealthInformation => {
	return {
		netWealthComments: wealthCommonDetails?.netWealthComments ?? null,
		netWealthAmount: wealthCommonDetails?.netWealthAmount ?? null
	};
};

// Client Income and Expenses

export interface ClientIncomeAndExpense {
	clientIncomeAndExpenseId: number;
	clientId?: number;
	clientGroupId: number;
	type: IncomeAndExpensesType;
	category: string;
	subCategory: string;
	monthlyAmount: number | null;
	percentageAllocation?: number;
}

export interface IncomeAndExpenseOptions {
	value?: string;
	text: string;
	category?: string;
	type?: IncomeAndExpensesType;
}

export const createDefaultClientIncomeAndExpense = (
	clientIncomeAndExpense: ClientIncomeAndExpense = null
): ClientIncomeAndExpense => {
	return {
		clientIncomeAndExpenseId: clientIncomeAndExpense?.clientIncomeAndExpenseId ?? null,
		clientId: clientIncomeAndExpense?.clientId ?? null,
		type: clientIncomeAndExpense?.type ?? null,
		category: clientIncomeAndExpense?.category ?? null,
		subCategory: clientIncomeAndExpense?.subCategory ?? null,
		monthlyAmount: clientIncomeAndExpense?.monthlyAmount ?? null,
		percentageAllocation: clientIncomeAndExpense?.percentageAllocation ?? null,
		clientGroupId: clientIncomeAndExpense?.clientGroupId ?? null
	};
};

export interface IncomeAndExpenseInformation {
	incomeAndExpenseComments: string | null;
	totalAmount: string | null;
}

export const createDefaultIncomeAndExpenseInformation = (
	incomeAndExpenseCommonDetails: IncomeAndExpenseInformation = null
): IncomeAndExpenseInformation => {
	return {
		incomeAndExpenseComments: incomeAndExpenseCommonDetails?.incomeAndExpenseComments ?? null,
		totalAmount: incomeAndExpenseCommonDetails?.totalAmount ?? defaultMoneyValue
	};
};

// Client Wealth Risk

export interface ClientWealthRisk {
	financialKnowledgeRating?: number;
	willingnessToTakeFinancialRisks?: string;
	howEasilyDoAdapt?: string;
	firstFinancialRiskWordComesToMind?: string;
	majorFinancialDecisionConcern?: string;
	degreeOfRiskPreparedToTake?: string;
	wouldYouBuySharesNow?: string;
	uncomfortableTotalInvestmentsValueGoDown?: string;
	mostAppealingInvestmentsMix?: string;
	valueDoesNotFallOrPurchasingPower?: string;
	expectedAverageRateOfReturn?: string;
	identifiedRiskProfile?: string;
	agreedRiskProfile?: string;
	wealthRiskNotes?: string;
}

export const createDefaultClientWealthRisk = (item: ClientWealthRisk = null): ClientWealthRisk => {
	return {
		financialKnowledgeRating: item?.financialKnowledgeRating,
		willingnessToTakeFinancialRisks: item?.willingnessToTakeFinancialRisks ?? null,
		howEasilyDoAdapt: item?.howEasilyDoAdapt ?? null,
		firstFinancialRiskWordComesToMind: item?.firstFinancialRiskWordComesToMind ?? null,
		majorFinancialDecisionConcern: item?.majorFinancialDecisionConcern ?? null,
		degreeOfRiskPreparedToTake: item?.degreeOfRiskPreparedToTake ?? null,
		wouldYouBuySharesNow: item?.wouldYouBuySharesNow ?? null,
		uncomfortableTotalInvestmentsValueGoDown: item?.uncomfortableTotalInvestmentsValueGoDown ?? null,
		mostAppealingInvestmentsMix: item?.mostAppealingInvestmentsMix ?? null,
		valueDoesNotFallOrPurchasingPower: item?.valueDoesNotFallOrPurchasingPower ?? null,
		expectedAverageRateOfReturn: item?.expectedAverageRateOfReturn ?? null
	};
};

export interface WealthRiskProfile {
	id: number;
	name: string;
	identifiedRiskProfile?: string;
	agreedRiskProfile?: string;
	wealthRiskNotes?: string;
}

export const createDefaultWealthRiskProfile = (item: WealthRiskProfile = null): WealthRiskProfile => {
	return {
		id: item?.id,
		name: item?.name,
		identifiedRiskProfile: item?.identifiedRiskProfile ?? null,
		agreedRiskProfile: item?.agreedRiskProfile ?? null,
		wealthRiskNotes: item?.wealthRiskNotes ?? null
	};
};

export interface ClientWealthInsurance {
	clientWealthInsuranceId?: number;
	clientId: number;
	clientGroupId: number;
	insuranceProvider: string;
	insuranceInSuper?: boolean;
	coverType: string;
	coverLevel?: string;
	premium?: number;
	additionalDetails?: string;
	sumInsured?: string;
}

export const createDefaultClientWealthInsurance = (item: ClientWealthInsurance = null): ClientWealthInsurance => {
	return {
		clientWealthInsuranceId: item?.clientWealthInsuranceId,
		clientId: item?.clientId ?? null,
		insuranceProvider: item?.insuranceProvider ?? null,
		insuranceInSuper: item?.insuranceInSuper ?? false,
		coverType: item?.coverType ?? null,
		coverLevel: item?.coverLevel ?? null,
		premium: item?.premium,
		additionalDetails: item?.additionalDetails ?? null,
		sumInsured: item?.sumInsured ?? null,
		clientGroupId: item?.clientGroupId ?? null
	};
};

export interface ClientWealthInsuranceCommonDetails {
	ableToLiveWithoutFinancialBurden?: boolean;
	haveAlternativeIncomeForIllness?: boolean;
	preferDebtFree?: boolean;
	preparedToSellAssets?: boolean;
	wishToProvideIncomeToDependants?: boolean;
	provideToDependantIncomeAmount?: number;
	provideToDependantIncomeYears?: number;
	canLiveWithoutIncomeDays?: string;
	rateYourHealth?: string;
	smoker?: boolean;
	havePrivateHealthInsurance?: boolean;
	involvedHazardous?: boolean;
	involvedHazardousDetails?: string;
	insuranceComments?: string;
}

export const createDefaultClientWealthInsuranceCommonDetails = (
	item: ClientWealthInsuranceCommonDetails = null
): ClientWealthInsuranceCommonDetails => {
	return {
		ableToLiveWithoutFinancialBurden: item?.ableToLiveWithoutFinancialBurden ?? false,
		haveAlternativeIncomeForIllness: item?.haveAlternativeIncomeForIllness ?? false,
		preferDebtFree: item?.preferDebtFree ?? false,
		preparedToSellAssets: item?.preparedToSellAssets ?? false,
		wishToProvideIncomeToDependants: item?.wishToProvideIncomeToDependants ?? false,
		provideToDependantIncomeAmount: item?.provideToDependantIncomeAmount,
		provideToDependantIncomeYears: item?.provideToDependantIncomeYears,
		canLiveWithoutIncomeDays: item?.canLiveWithoutIncomeDays ?? null,
		rateYourHealth: item?.rateYourHealth ?? null,
		smoker: item?.smoker ?? false,
		havePrivateHealthInsurance: item?.havePrivateHealthInsurance ?? false,
		involvedHazardous: item?.involvedHazardous ?? false,
		involvedHazardousDetails: item?.involvedHazardousDetails ?? null,
		insuranceComments: item?.insuranceComments ?? null
	};
};

export interface ClientScopeOfAdvice {
	initialScopeOfAdvice?: string;
	agreedScopeOfAdvice?: string;
	hasClientLimitedTheAdvice?: boolean;
	adviceLimitations?: string;
}

export const createDefaultClientScopeOfAdvice = (item: ClientScopeOfAdvice = null): ClientScopeOfAdvice => {
	return {
		initialScopeOfAdvice: item?.initialScopeOfAdvice ?? null,
		agreedScopeOfAdvice: item?.agreedScopeOfAdvice ?? null,
		hasClientLimitedTheAdvice: item?.hasClientLimitedTheAdvice ?? false,
		adviceLimitations: item?.adviceLimitations ?? null
	};
};

export interface ClientWealthAcknowledgement {
	acknowledgementSignature?: string;
	acknowledgementDate?: Date;
	acknowledgementIpAddress?: string;
	acknowledgementEmail?: string;
	financialAdviserSignature?: string;
	financialAdviserDeclarationDate?: Date;
	financialAdviserAdUserId?: string;
	financialAdviserEmail?: string;
	financialAdviserName?: string;
}

export const createDefaultClientWealthAcknowledgement = (
	item: ClientWealthAcknowledgement = null
): ClientWealthAcknowledgement => {
	return {
		acknowledgementSignature: item?.acknowledgementSignature ?? null,
		acknowledgementDate: item?.acknowledgementDate,
		acknowledgementIpAddress: item?.acknowledgementIpAddress ?? null,
		acknowledgementEmail: item?.acknowledgementEmail ?? null,
		financialAdviserSignature: item?.financialAdviserSignature ?? null,
		financialAdviserDeclarationDate: item?.financialAdviserDeclarationDate,
		financialAdviserAdUserId: item?.financialAdviserAdUserId ?? null,
		financialAdviserEmail: item?.financialAdviserEmail ?? null,
		financialAdviserName: item?.financialAdviserName ?? null
	};
};

export interface ClientWealthAuthorisation {
	companyName?: string;
	productDetails?: string;
	authorisationSignature?: string;
	authorisationIpAddress?: string;
	authorisationDate?: Date;
	authorisationEmail?: string;
	clientName?: string;
	clientDOB?: string;
	clientAddress?: string;
}

export const createDefaultClientWealthAuthorisation = (
	item: ClientWealthAuthorisation = null
): ClientWealthAuthorisation => {
	return {
		companyName: item?.companyName ?? null,
		productDetails: item?.productDetails ?? null,
		authorisationSignature: item?.authorisationSignature ?? null,
		authorisationIpAddress: item?.authorisationIpAddress ?? null,
		authorisationDate: item?.authorisationDate,
		authorisationEmail: item?.authorisationEmail ?? null,
		clientName: item?.clientName ?? null,
		clientDOB: item?.clientDOB ?? null,
		clientAddress: item?.clientAddress ?? null
	};
};

export interface PersonalDocumentsInformation {
	hasOwnHomeDocuments: boolean | null;
	hasInvestmentPropertyDocuments: boolean | null;
	hasUnitTrustCertificatesDocuments: boolean | null;
	hasTermDepositAndBondDocuments: boolean | null;
	hasShareCertificatesDocuments: boolean | null;
	hasHealthInsuranceDocuments: boolean | null;
	hasLifeInsuranceDocuments: boolean | null;
	hasHomeInsuranceDocuments: boolean | null;
	hasMotorVehicleInsuranceDocuments: boolean | null;
	hasOtherInsuranceDocuments: boolean | null;
	hasCurrentSuperStatementDocuments: boolean | null;
	hasSmsfAccountsDocuments: boolean | null;
	hasPreviousEmployerFundsDocuments: boolean | null;
	hasPastSuperStatementDocuments: boolean | null;
	hasDebtAgreementsDocuments: boolean | null;
	hasLastWillDocuments: boolean | null;
	hasTrustDocuments: boolean | null;
	hasSaleOfAssetsDocuments: boolean | null;
	hasFinanceReviewDocuments: boolean | null;
	hasOtherPersonalDocuments: boolean | null;
	otherFinancialInformation: string;
}

export const createDefaultPersonalDocumentsInformation = (
	item: PersonalDocumentsInformation = null
): PersonalDocumentsInformation => {
	return {
		hasOwnHomeDocuments: item?.hasOwnHomeDocuments, // Should NOT default to a value as null is radio group field option
		hasInvestmentPropertyDocuments: item?.hasInvestmentPropertyDocuments,
		hasUnitTrustCertificatesDocuments: item?.hasUnitTrustCertificatesDocuments,
		hasTermDepositAndBondDocuments: item?.hasTermDepositAndBondDocuments,
		hasShareCertificatesDocuments: item?.hasShareCertificatesDocuments,
		hasHealthInsuranceDocuments: item?.hasHealthInsuranceDocuments,
		hasLifeInsuranceDocuments: item?.hasLifeInsuranceDocuments,
		hasHomeInsuranceDocuments: item?.hasHomeInsuranceDocuments,
		hasMotorVehicleInsuranceDocuments: item?.hasMotorVehicleInsuranceDocuments,
		hasOtherInsuranceDocuments: item?.hasOtherInsuranceDocuments,
		hasCurrentSuperStatementDocuments: item?.hasCurrentSuperStatementDocuments,
		hasSmsfAccountsDocuments: item?.hasSmsfAccountsDocuments,
		hasPreviousEmployerFundsDocuments: item?.hasPreviousEmployerFundsDocuments,
		hasPastSuperStatementDocuments: item?.hasPastSuperStatementDocuments,
		hasDebtAgreementsDocuments: item?.hasDebtAgreementsDocuments,
		hasLastWillDocuments: item?.hasLastWillDocuments,
		hasTrustDocuments: item?.hasTrustDocuments,
		hasSaleOfAssetsDocuments: item?.hasSaleOfAssetsDocuments,
		hasFinanceReviewDocuments: item?.hasFinanceReviewDocuments,
		hasOtherPersonalDocuments: item?.hasOtherPersonalDocuments,
		otherFinancialInformation: item?.otherFinancialInformation
	};
};

export interface ClientCurrentEstateDetails {
	haveExistingWill?: boolean;
	originalWillDate?: Date;
	willReviewedDate?: Date;
	willExecutors?: string;
	willGuardianName?: string;
	willBeneficiaries?: string;
	willLocation?: string;
	powerOfAttorney: string;
	expectedInheritances: number | null;
	expectedInheritancesDetails: string;
	includeProvisionsForFuneralExpenses: boolean | null;
	haveWillCircumstancesChanged?: boolean;
	haveTestamentaryTrust?: boolean;
	testamentaryTrustBeneficiaries?: string;
	havePowerofAttorney?: boolean;
	powerofAttorneyName?: string;
	haveFuneralPlan?: boolean;
	funeralDocuments?: string;
	haveImpactOnEstatePlanning?: boolean;
	haveBequests?: boolean;
	estatePlanningIssues?: string;
	beneficiariesInAVulnerableSituation?: string;
	estatePlanningAdditionalInfo?: string;
}

export const createDefaultClientCurrentEstateDetails = (
	item: ClientCurrentEstateDetails = null
): ClientCurrentEstateDetails => {
	return {
		haveExistingWill: item?.haveExistingWill ?? false,
		originalWillDate: item?.originalWillDate,
		willReviewedDate: item?.willReviewedDate,
		willExecutors: item?.willExecutors ?? null,
		willGuardianName: item?.willGuardianName ?? null,
		willBeneficiaries: item?.willBeneficiaries ?? null,
		willLocation: item?.willLocation ?? null,
		powerOfAttorney: item?.powerOfAttorney ?? null,
		expectedInheritances: item?.expectedInheritances ?? null,
		expectedInheritancesDetails: item?.expectedInheritancesDetails ?? null,
		includeProvisionsForFuneralExpenses: item?.includeProvisionsForFuneralExpenses ?? false,
		haveWillCircumstancesChanged: item?.haveWillCircumstancesChanged ?? false,
		haveTestamentaryTrust: item?.haveTestamentaryTrust ?? false,
		testamentaryTrustBeneficiaries: item?.testamentaryTrustBeneficiaries ?? null,
		havePowerofAttorney: item?.havePowerofAttorney ?? false,
		powerofAttorneyName: item?.powerofAttorneyName ?? null,
		haveFuneralPlan: item?.haveFuneralPlan ?? false,
		funeralDocuments: item?.funeralDocuments ?? null,
		haveImpactOnEstatePlanning: item?.haveImpactOnEstatePlanning ?? false,
		haveBequests: item?.haveBequests ?? false,
		estatePlanningIssues: item?.estatePlanningIssues ?? null,
		beneficiariesInAVulnerableSituation: item?.beneficiariesInAVulnerableSituation ?? null,
		estatePlanningAdditionalInfo: item?.estatePlanningAdditionalInfo ?? null
	};
};

export interface ClientEstatePlanning {
	requireWill?: boolean;
	requirePowerofAttorney?: boolean;
	requirePowerofGuardianship?: boolean;
	includeMedicalGuardianship?: boolean | null;
	requireCorporatePowerofGuardianship?: boolean | null;
	includeDeathBenefitNominee?: boolean | null;
	requireAgedCarePlan?: boolean | null;
	estateReviewDate?: Date | null;
	planningRequirements?: string;
	planningComments?: string;
}

export const createDefaultClientEstatePlanning = (item: ClientEstatePlanning = null): ClientEstatePlanning => {
	return {
		requireWill: item?.requireWill ?? false,
		requirePowerofAttorney: item?.requirePowerofAttorney ?? false,
		requirePowerofGuardianship: item?.requirePowerofGuardianship ?? false,
		includeMedicalGuardianship: item?.includeMedicalGuardianship ?? false,
		requireCorporatePowerofGuardianship: item?.requireCorporatePowerofGuardianship ?? false,
		includeDeathBenefitNominee: item?.includeDeathBenefitNominee ?? false,
		requireAgedCarePlan: item?.requireAgedCarePlan ?? false,
		estateReviewDate: item?.estateReviewDate,
		planningRequirements: item?.planningRequirements ?? null,
		planningComments: item?.planningComments ?? null
	};
};

export interface ProfileSubsectionCompletionDetails {
	tabIndex?: number;
	urlSlug?: string;
	subsection?: string;
	isCompleted?: number;
}
